import type { CoachFeedInterceptor } from './sync';
import type { CoachEventLike, CoachFeedFrameLike } from './types';

import { CoachFrameKind } from './types';

/**
 * The dev panel's chaos and block modes, aimed at the coach feed.
 *
 * Both exist to prove one rule: a gap or a reconnect sends the client to
 * `coachSession(cursor: lastSeq)` and nothing else repairs anything.
 *
 * - **chaos** swallows durable frames at random, so the next one that lands is out of order and
 *   gap-detect fires mid-session. PROGRESS is left alone — it carries no seq and dropping it would
 *   prove nothing.
 * - **block** swallows everything, so the feed goes silent while the session keeps moving. Leaving
 *   block replays the reconnect path, and the catch-up read is what restores the transcript.
 *
 * Module state rather than a composable: the toggle lives in the dev panel and the subscriber is
 * whatever surface is running the feed, and they never share a component tree. Every export here is
 * part of the library surface fe-relayer builds the product client on.
 */

export const CoachFeedChaosMode = {
  None: 'none',
  Chaos: 'chaos',
  Block: 'block',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CoachFeedChaosMode = (typeof CoachFeedChaosMode)[keyof typeof CoachFeedChaosMode];

const DROP_PROBABILITY = 0.5;

let mode: CoachFeedChaosMode = CoachFeedChaosMode.None;
const resumeListeners = new Set<() => void>();

export const getCoachFeedChaosMode = (): CoachFeedChaosMode => mode;

export const setCoachFeedChaosMode = (next: CoachFeedChaosMode): void => {
  if (next === mode) {
    return;
  }

  const wasSwallowingEverything = mode === CoachFeedChaosMode.Block;
  mode = next;

  if (wasSwallowingEverything && next === CoachFeedChaosMode.None) {
    for (const listener of resumeListeners) {
      listener();
    }
  }
};

export const coachFeedChaosInterceptor = <TEvent extends CoachEventLike>(): CoachFeedInterceptor<TEvent> => ({
  accept: (frame: CoachFeedFrameLike<TEvent>) => {
    if (mode === CoachFeedChaosMode.Block) {
      return false;
    }
    if (mode === CoachFeedChaosMode.Chaos && frame.kind !== CoachFrameKind.Progress) {
      return Math.random() >= DROP_PROBABILITY;
    }
    return true;
  },
  onResume: (listener: () => void) => {
    resumeListeners.add(listener);
    return () => resumeListeners.delete(listener);
  },
});
